import React, { useState } from 'react';
import { Alert, AlertSeverity, AlertStatus, AlertOwner } from '@cbm/shared';
import { AlertCard, CompactAlertCard } from './AlertCard';
import { BulkAlertActions } from './AlertActions';
import { Search, Filter, SortAsc, SortDesc } from 'lucide-react';

interface AlertListProps {
  alerts: Alert[];
  variant?: 'full' | 'compact';
  showBulkActions?: boolean;
  showFilters?: boolean;
  showSearch?: boolean;
  onAcknowledge?: (alertId: string) => void;
  onClose?: (alertId: string) => void;
  onViewDetails?: (alertId: string) => void;
  onViewEvidence?: (alertId: string) => void;
  onRequestExplanation?: (alertId: string) => void;
  onSelectAlert?: (alert: Alert) => void;
  className?: string;
}

interface AlertFilters {
  severity?: AlertSeverity[];
  status?: AlertStatus[];
  owner?: AlertOwner[];
  machineId?: string;
  search?: string;
}

type SortField = 'createdAt' | 'severity' | 'status' | 'machineId';
type SortDirection = 'asc' | 'desc';

export function AlertList({
  alerts,
  variant = 'full',
  showBulkActions = false,
  showFilters = true,
  showSearch = true,
  onAcknowledge,
  onClose,
  onViewDetails,
  onViewEvidence,
  onSelectAlert,
  className = ''
}: AlertListProps) {
  const [selectedAlerts, setSelectedAlerts] = useState<string[]>([]);
  const [filters, setFilters] = useState<AlertFilters>({});
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Filter alerts based on current filters
  const filteredAlerts = React.useMemo(() => {
    let filtered = [...alerts];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(alert =>
        alert.type.toLowerCase().includes(searchLower) ||
        alert.summary.toLowerCase().includes(searchLower) ||
        alert.machineId.toLowerCase().includes(searchLower)
      );
    }

    // Apply severity filter
    if (filters.severity?.length) {
      filtered = filtered.filter(alert => filters.severity!.includes(alert.severity));
    }

    // Apply status filter
    if (filters.status?.length) {
      filtered = filtered.filter(alert => filters.status!.includes(alert.status));
    }

    // Apply owner filter
    if (filters.owner?.length) {
      filtered = filtered.filter(alert => filters.owner!.includes(alert.owner));
    }

    // Apply machine filter
    if (filters.machineId) {
      filtered = filtered.filter(alert => 
        alert.machineId.toLowerCase().includes(filters.machineId!.toLowerCase())
      );
    }

    return filtered;
  }, [alerts, filters]);

  // Sort alerts
  const sortedAlerts = React.useMemo(() => {
    const sorted = [...filteredAlerts];
    
    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'severity':
          const severityOrder = { LATE: 3, MID: 2, EARLY: 1 };
          aValue = severityOrder[a.severity];
          bValue = severityOrder[b.severity];
          break;
        case 'status':
          const statusOrder = { OPEN: 3, ACKNOWLEDGED: 2, CLOSED: 1 };
          aValue = statusOrder[a.status];
          bValue = statusOrder[b.status];
          break;
        case 'machineId':
          aValue = a.machineId;
          bValue = b.machineId;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredAlerts, sortField, sortDirection]);

  // Handle sort change (placeholder for future implementation)
  // const handleSort = (field: SortField) => {
  //   if (sortField === field) {
  //     setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  //   } else {
  //     setSortField(field);
  //     setSortDirection('desc');
  //   }
  // };

  // Handle selection
  const handleSelectAlert = (alertId: string, selected: boolean) => {
    if (selected) {
      setSelectedAlerts(prev => [...prev, alertId]);
    } else {
      setSelectedAlerts(prev => prev.filter(id => id !== alertId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedAlerts(sortedAlerts.map(alert => alert.alertId));
    } else {
      setSelectedAlerts([]);
    }
  };

  // Handle bulk actions
  const handleBulkAcknowledge = (alertIds: string[]) => {
    alertIds.forEach(id => onAcknowledge?.(id));
    setSelectedAlerts([]);
  };

  const handleBulkClose = (alertIds: string[]) => {
    alertIds.forEach(id => onClose?.(id));
    setSelectedAlerts([]);
  };

  const selectedAlertObjects = sortedAlerts.filter(alert => 
    selectedAlerts.includes(alert.alertId)
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search and filters */}
      {(showSearch || showFilters) && (
        <div className="flex items-center space-x-4">
          {/* Search */}
          {showSearch && (
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search alerts..."
                value={filters.search || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Filter toggle */}
          {showFilters && (
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`
                flex items-center space-x-2 px-4 py-2 border rounded-lg font-medium
                ${showFilterPanel 
                  ? 'border-blue-300 text-blue-700 bg-blue-50' 
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }
              `}
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          )}

          {/* Sort controls */}
          <div className="flex items-center space-x-2">
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value as SortField)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="createdAt">Created</option>
              <option value="severity">Severity</option>
              <option value="status">Status</option>
              <option value="machineId">Machine</option>
            </select>
            <button
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {sortDirection === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      {/* Filter panel */}
      {showFilterPanel && (
        <div className="p-4 bg-gray-50 border rounded-lg space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Severity filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
              <div className="space-y-1">
                {(['EARLY', 'MID', 'LATE'] as AlertSeverity[]).map(severity => (
                  <label key={severity} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.severity?.includes(severity) || false}
                      onChange={(e) => {
                        const current = filters.severity || [];
                        if (e.target.checked) {
                          setFilters(prev => ({ ...prev, severity: [...current, severity] }));
                        } else {
                          setFilters(prev => ({ ...prev, severity: current.filter(s => s !== severity) }));
                        }
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">{severity}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Status filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="space-y-1">
                {(['OPEN', 'ACKNOWLEDGED', 'CLOSED'] as AlertStatus[]).map(status => (
                  <label key={status} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.status?.includes(status) || false}
                      onChange={(e) => {
                        const current = filters.status || [];
                        if (e.target.checked) {
                          setFilters(prev => ({ ...prev, status: [...current, status] }));
                        } else {
                          setFilters(prev => ({ ...prev, status: current.filter(s => s !== status) }));
                        }
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">{status}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Owner filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Owner</label>
              <div className="space-y-1">
                {(['PLANT', 'OEM'] as AlertOwner[]).map(owner => (
                  <label key={owner} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.owner?.includes(owner) || false}
                      onChange={(e) => {
                        const current = filters.owner || [];
                        if (e.target.checked) {
                          setFilters(prev => ({ ...prev, owner: [...current, owner] }));
                        } else {
                          setFilters(prev => ({ ...prev, owner: current.filter(o => o !== owner) }));
                        }
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">{owner}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Machine filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Machine ID</label>
              <input
                type="text"
                placeholder="Filter by machine..."
                value={filters.machineId || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, machineId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          {/* Clear filters */}
          <div className="flex justify-end">
            <button
              onClick={() => setFilters({})}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}

      {/* Bulk actions */}
      {showBulkActions && (
        <BulkAlertActions
          selectedAlerts={selectedAlertObjects}
          onBulkAcknowledge={handleBulkAcknowledge}
          onBulkClose={handleBulkClose}
          onClearSelection={() => setSelectedAlerts([])}
        />
      )}

      {/* Results summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          Showing {sortedAlerts.length} of {alerts.length} alerts
        </span>
        
        {showBulkActions && (
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={selectedAlerts.length === sortedAlerts.length && sortedAlerts.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            <span>Select all</span>
          </label>
        )}
      </div>

      {/* Alert list */}
      <div className="space-y-3">
        {sortedAlerts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No alerts found matching your criteria
          </div>
        ) : (
          sortedAlerts.map(alert => (
            <div key={alert.alertId} className="relative">
              {showBulkActions && (
                <div className="absolute left-3 top-3 z-10">
                  <input
                    type="checkbox"
                    checked={selectedAlerts.includes(alert.alertId)}
                    onChange={(e) => handleSelectAlert(alert.alertId, e.target.checked)}
                    className="rounded"
                  />
                </div>
              )}
              
              {variant === 'compact' ? (
                <div onClick={() => onSelectAlert?.(alert)} className="cursor-pointer">
                  <CompactAlertCard
                    alert={alert}
                    onAcknowledge={onAcknowledge}
                    onClose={onClose}
                    className={showBulkActions ? 'ml-8' : ''}
                  />
                </div>
              ) : (
                <div onClick={() => onSelectAlert?.(alert)} className="cursor-pointer">
                  <AlertCard
                    alert={alert}
                    onAcknowledge={onAcknowledge}
                    onClose={onClose}
                    onViewDetails={onViewDetails}
                    onViewEvidence={onViewEvidence}
                    className={showBulkActions ? 'ml-8' : ''}
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}